import { useState, useEffect } from 'react'

export enum ScrollDirection {
  INIT = 'INIT',
  UP = 'UP',
  DOWN = 'DOWN',
}

interface UseScrollProps {
  throttleDelay?: number
}

const globalWindow = typeof window === 'undefined' ? undefined : window

const getScroll = () => ({
  x: Math.round(
    (globalWindow?.pageXOffset ||
      globalWindow?.document.documentElement.scrollLeft) ??
      0,
  ),
  y: Math.round(
    (globalWindow?.pageYOffset ||
      globalWindow?.document.documentElement.scrollTop) ??
      0,
  ),
})

const throttle = (fn: (event: Event) => void, wait: number) => {
  let last = 0
  return (event: Event) => {
    const now = Date.now()
    if (now - last >= wait) {
      fn(event)
      last = now
    }
  }
}

const useScroll = ({ throttleDelay = 100 }: UseScrollProps = {}) => {
  const [position, setPosition] = useState({ x: 0, y: 0 })
  const [direction, setDirection] = useState(ScrollDirection.INIT)

  useEffect(() => {
    let lastScrollY = getScroll().y
    let scrollTimeout: number | null | undefined = null

    const handleScroll = throttle(() => {
      if (scrollTimeout) {
        globalWindow?.cancelAnimationFrame(scrollTimeout)
      }
      scrollTimeout = globalWindow?.requestAnimationFrame(() => {
        const scroll = getScroll()
        const direction =
          scroll.y > lastScrollY ? ScrollDirection.DOWN : ScrollDirection.UP
        lastScrollY = scroll.y > 0 ? scroll.y : 0

        setPosition(scroll)
        setDirection(direction)
      })
    }, throttleDelay)

    globalWindow?.addEventListener('scroll', handleScroll, { passive: true })

    return () => globalWindow?.removeEventListener('scroll', handleScroll)
  }, [throttleDelay])

  return {
    direction,
    position,
  }
}

export default useScroll
